import type { Metadata } from "next";

import { routing } from "@/i18n/routing";

// Resolved at build time. Vercel preview deploys, Docker stages, and
// local production builds can override via NEXT_PUBLIC_SITE_URL so OG
// images, canonical links, and sitemap entries point at the right host.
// The production fallback is the registered domain.
export const SITE_URL =
  process.env.NEXT_PUBLIC_SITE_URL ?? "https://maidijital.com";
export const SITE_NAME = "Mai Dijital";

type AppLocale = (typeof routing.locales)[number];

const OG_LOCALE_MAP: Record<AppLocale, string> = {
  tr: "tr_TR",
  en: "en_US",
};

function absoluteUrl(path: string, locale: AppLocale) {
  const normalized = path === "/" ? "" : path.startsWith("/") ? path : `/${path}`;
  return `${SITE_URL}/${locale}${normalized}`;
}

function ogImageUrl(params: {
  locale: AppLocale;
  title: string;
  eyebrow?: string;
}) {
  const search = new URLSearchParams({
    title: params.title,
    locale: params.locale,
  });
  if (params.eyebrow) search.set("eyebrow", params.eyebrow);
  return `${SITE_URL}/api/og?${search.toString()}`;
}

function resolveLocale(locale: string): AppLocale {
  return (routing.locales as readonly string[]).includes(locale)
    ? (locale as AppLocale)
    : routing.defaultLocale;
}

// SERPs typically truncate descriptions around 155-160 chars. We clamp
// at 160 with an ellipsis to avoid mid-word cuts. Title clamps to 70.
const DESCRIPTION_MAX = 160;
const TITLE_MAX = 70;

function clampWithEllipsis(value: string, max: number): string {
  if (value.length <= max) return value;
  return `${value.slice(0, max - 1).trimEnd()}…`;
}

export type BuildMetadataInput = {
  locale: string;
  path: string;
  title: string;
  description: string;
  eyebrow?: string;
};

export function buildMetadata({
  locale: rawLocale,
  path,
  title,
  description,
  eyebrow,
}: BuildMetadataInput): Metadata {
  const locale = resolveLocale(rawLocale);
  const canonical = absoluteUrl(path, locale);
  const safeTitle = clampWithEllipsis(title, TITLE_MAX);
  const safeDescription = clampWithEllipsis(description, DESCRIPTION_MAX);
  const fullTitle = `${safeTitle} · ${SITE_NAME}`;
  const otherLocale: AppLocale = locale === "tr" ? "en" : "tr";
  const image = ogImageUrl({ locale, title: safeTitle, eyebrow });

  return {
    title: safeTitle,
    description: safeDescription,
    alternates: {
      canonical,
      languages: {
        tr: absoluteUrl(path, "tr"),
        en: absoluteUrl(path, "en"),
        "x-default": absoluteUrl(path, routing.defaultLocale),
      },
    },
    robots: {
      index: true,
      follow: true,
      googleBot: {
        index: true,
        follow: true,
        "max-image-preview": "large",
        "max-snippet": -1,
        "max-video-preview": -1,
      },
    },
    openGraph: {
      title: fullTitle,
      description: safeDescription,
      url: canonical,
      siteName: SITE_NAME,
      locale: OG_LOCALE_MAP[locale],
      alternateLocale: OG_LOCALE_MAP[otherLocale],
      type: "website",
      images: [
        {
          url: image,
          width: 1200,
          height: 630,
          alt: eyebrow ? `${safeTitle} — ${eyebrow}` : safeTitle,
        },
      ],
    },
    twitter: {
      card: "summary_large_image",
      title: fullTitle,
      description: safeDescription,
      images: [image],
    },
  };
}
